'use strict';

import angular from 'angular';
import {accessLevels} from './routing-config';

const loginServices = angular.module('loginModule.loginService', []);

loginServices.provider('loginService', function () {
    const userToken = localStorage.getItem('appsoup:userToken');
    const errorState = 'appsoup.error';
    const loginState = 'appsoup.login';

    this.$get = [
        '$rootScope',
        '$http',
        '$q',
        '$timeout',
        '$state',
        '$modalStack',
        'growl',
        'accessService',
        'LanguageService',
        'SystemModules',
        'AppsoupAPI',
        function (
            $rootScope,
            $http,
            $q,
            $timeout,
            $state,
            $modalStack,
            growl,
            accessService,
            LanguageService,
            SystemModules,
            AppsoupAPI
        ) {

            // Forward declarations for functions used in wrappedService
            let setToken;
            let redirectToModule;

            /**
             * Low-level, private functions.
             */
            const setHeaders = (token) => {
                if (!token) {
                    delete $http.defaults.headers.common.Authorization;

                    return;
                }
                $http.defaults.headers.common.Authorization = `Bearer ${token.toString()}`;
            };

            setToken = (token) => {
                if (!token) {
                    localStorage.removeItem('appsoup:userToken');
                } else {
                    localStorage.setItem('appsoup:userToken', token);
                }
                setHeaders(token);
            };

            const getLoginData = () => {
                if (userToken) {
                    setHeaders(userToken);
                } else {
                    wrappedService.userRole = accessLevels.public;
                    wrappedService.isLogged = false;
                    wrappedService.doneLoading = true;
                }
            };

            redirectToModule = (module, isLoginPage) => {
                const stateName = SystemModules.getDefaultState(module);
                wrappedService.defaultState = stateName;

                if (isLoginPage) {
                    const requested = wrappedService.requestedStateChange;
                    $timeout(() => {
                        if (requested && requested.to) {
                            $state.go(requested.to.name, requested.toParams);
                        } else {
                            $state.go(stateName);
                        }
                    });
                }
            };

            const managePermissions = () => {
                // Register routing function.
                $rootScope.$on('$stateChangeStart', (event, to, toParams) => {

                    $modalStack.dismissAll();

                    if (angular.isDefined(to) &&
                        angular.isDefined(to.name) &&
                        to.name !== 'appsoup.login') {
                        wrappedService.requestedStateChange = {
                            to: to,
                            toParams: toParams
                        };
                    }

                    if (wrappedService.userRole === null) {
                        wrappedService.doneLoading = false;
                        wrappedService.pendingStateChange = {
                            to: to,
                            toParams: toParams
                        };
                        return;
                    }

                    if (angular.isUndefined(to.accessLevel) ||
                        angular.isUndefined(wrappedService.userRole.titleMask) ||
                        (wrappedService.userRole.titleMask.indexOf(to.accessLevel.title) !== -1)) {
                        angular.noop();
                    } else {
                        event.preventDefault();
                        $rootScope.$emit('$statePermissionError');
                        if (wrappedService.isLogged) {
                            $state.go(errorState, {error: 'unauthorized'}, {location: false, inherit: false});
                        } else {
                            $state.go(loginState);
                            $timeout(() => {
                                growl.error('ERROR.NOT_LOGGED_IN');
                            });
                        }
                    }
                });

                $rootScope.$on('$stateChangeError', (event, to, toParams, from, fromParams, error) => {
                    let redirectObj;

                    error = (typeof error === 'object') ? error.status.toString() : error;

                    if (/^[45]\d{2}$/.test(error)) {
                        wrappedService.logoutUser();
                    }

                    wrappedService.requestedStateChange = {
                        to: to,
                        toParams: toParams
                    };

                    if (angular.isDefined(to.redirectMap) &&
                        angular.isDefined(to.redirectMap[error])) {
                        if (typeof to.redirectMap[error] === 'string') {
                            return $state.go(to.redirectMap[error], {error}, {location: false, inherit: false});
                        } else if (typeof to.redirectMap[error] === 'object') {
                            redirectObj = to.redirectMap[error];
                            return $state.go(redirectObj.state, {error: redirectObj.prefix + error}, {
                                location: false,
                                inherit: false
                            });
                        }
                    }

                    return $state.go(errorState, {error}, {location: false, inherit: false});
                });

                // If any other service throws 'forbidden' error
                $rootScope.$on('userNotLoggedIn', () => {
                    wrappedService.logoutUser();
                });

                $rootScope.$on('404', () => {
                    return $state.go(errorState, {error: '404'}, {location: false, inherit: false});
                });
            };

            /**
             * High level, public methods
             */
            const wrappedService = {
                loginHandler: (data, status, headers, config) => {

                    if (data.success && angular.isDefinedNotNull(data.user)) {
                        const headerToken = config.headers.Authorization;

                        if (angular.isNullOrUndefined(headerToken)) {
                            setToken(data.token);
                        }

                        angular.extend(wrappedService.user, data.user);
                        wrappedService.isLogged = true;

                        if (data.user.superadmin) {
                            wrappedService.userRole = accessService.getSuperUserRoles();
                        } else {
                            wrappedService.userRole = accessService.getUserRoles(data.menu);
                        }

                        LanguageService.changeTo(data.user.lang)
                            .then(() => {
                                const isLoginPage = (config.url !== AppsoupAPI.API_URL + '/auth-check');
                                $rootScope.$broadcast('newApp');
                                redirectToModule(data.redirect, isLoginPage);
                            });
                    } else if (angular.isDefined(data.error)) {
                        growl.error(data.error);
                    }

                    return data;
                },
                loginUser: function (httpPromise) {
                    httpPromise.then(response => this.loginHandler(response.data, response.status, response.headers, response.config));
                },
                logoutUser: function (httpPromise) {
                    setToken(null);
                    this.userRole = accessLevels.public;
                    this.user = {};
                    this.isLogged = false;

                    if (angular.isDefined(httpPromise)) {
                        $rootScope.doingResolve = true;
                        httpPromise.then(
                            () => {
                                $state.go(loginState);
                            })
                            .finally(() => {
                                $rootScope.doingResolve = false;
                            });
                    } else {
                        $state.go(loginState);
                        $rootScope.doingResolve = false;
                    }
                },
                resolvePendingState: function (httpPromise) {
                    const checkUser = $q.defer();
                    const self = this;
                    const pendingState = self.pendingStateChange;

                    httpPromise.then(response => self.loginHandler(response.data, response.status, response.headers, response.config));

                    httpPromise.then(
                        (httpObj) => {
                            self.doneLoading = true;
                            if (httpObj.data.success &&
                                (angular.isUndefined(pendingState) || pendingState === null ||
                                    angular.isUndefined(pendingState.to) || pendingState.to === null ||
                                    angular.isUndefined(pendingState.to.accessLevel) ||
                                    angular.isUndefined(self.userRole.titleMask) ||
                                    (self.userRole.titleMask.indexOf(pendingState.to.accessLevel.title) !== -1))) {
                                checkUser.resolve();
                            } else {
                                checkUser.reject('unauthorized');
                                self.logoutUser();
                            }
                        },
                        (httpObj) => {
                            checkUser.reject(httpObj.status.toString());
                        }
                    );

                    self.pendingStateChange = null;

                    return checkUser.promise;
                },
                /**
                 * Public properties
                 */
                userRole: null,
                user: {},
                isLogged: null,
                pendingStateChange: null,
                doneLoading: null
            };

            getLoginData();
            managePermissions();

            return wrappedService;
        }
    ];

});

loginServices.service('accessService', function () {
    this.getUserRoles = (menu) => {
        let bitMask = accessLevels.public.bitMask;
        const titleMask = [accessLevels.public.title];
        angular.forEach(menu, (module) => {
            angular.forEach(accessLevels, (systemRole) => {
                if (module === systemRole.title) {
                    bitMask = bitMask | systemRole.bitMask;
                    titleMask.push(systemRole.title);
                }
            });
        });

        return {bitMask: bitMask, roles: menu, titleMask: titleMask};
    };

    this.getSuperUserRoles = () => {
        let bitMask = accessLevels.public.bitMask;
        angular.forEach(accessLevels, (systemRole) => {
            bitMask = bitMask | systemRole.bitMask;
        });

        return {bitMask: bitMask, roles: accessLevels};
    };

    this.userHasPermission = (state, userRole) => {
        let accessLevel, userBitMask, userTitleMask;

        if (angular.isDefinedNotNull(state)) {
            accessLevel = state.accessLevel;
        }

        if (angular.isDefinedNotNull(userRole)) {
            userBitMask = userRole.bitMask;
            userTitleMask = userRole.titleMask;
        }

        if (angular.isDefined(userTitleMask)) {
            return (angular.isUndefined(accessLevel) || (userTitleMask.indexOf(accessLevel.title) !== -1)); // uprawnienia zwyklego uzytkownika
        }
        return (angular.isUndefined(accessLevel) || userBitMask === -1); // uprawnienia superadmina
    };
});


loginServices.service('Permissions', ['loginService', function (loginService) {

    this.hasWriteAccess = (module, advancedMode) => {
        const user = loginService.user;
        let hasAccess = user.superadmin || false;

        if (angular.isDefined(user.role)) {
            /* beacon, campaign, place, report, user, application */
            const _module = user.role[module];

            if (angular.isDefined(_module)) {
                if (advancedMode) {
                    hasAccess = _module > 4;
                } else {
                    hasAccess = _module > 1 && _module !== 4 && _module !== 5;
                }
            } else {
                throw new Error(`No such module! Could not determine permissions for: ${module} module!`);
            }
        }

        return hasAccess;
    };

    this.hasAnyAccess = (module) => {
        const user = loginService.user;
        let hasAccess = user.superadmin || false;

        if (angular.isDefined(user.role)) {
            /* beacon, campaign, place, report, user, application */
            const _module = user.role[module];

            if (angular.isDefined(_module)) {
                hasAccess = _module > 0;
            } else {
                throw new Error(`No such module! Could not determine permissions for: ${module} module!`);
            }
        }

        return hasAccess;
    };

    this.hasSuperAdminAccess = () => {
        return loginService.user.superadmin;
    };
}]);
